import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Bot, ArrowRight, BarChart3, ReceiptCent } from 'lucide-react';

export default function IAChatDemo() {
  return (
    <section className="py-24 relative overflow-hidden font-sans bg-[#FCFCFD]">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="flex flex-col lg:flex-row items-center gap-20">
          
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-14 h-14 bg-violet-100 rounded-3xl flex items-center justify-center mb-8 mx-auto lg:mx-0 shadow-sm border border-violet-200/50"
            >
              <Bot className="w-7 h-7 text-violet-600" />
            </motion.div>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-6xl font-black text-slate-900 mb-8 leading-[1.1] tracking-tighter"
            >
               O fim das planilhas.<br/>
               <span className="text-slate-400 font-light">Tudo pelo chat.</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-slate-500 text-xl mb-12 leading-relaxed font-light max-w-lg mx-auto lg:mx-0"
            >
              Conectado nativamente ao seu WhatsApp e Telegram, o LukIA entende linguagem natural humana. Faça uma pergunta simples e ele te devolve insights ultra-processados na hora.
            </motion.p>

            <motion.button 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group bg-slate-900 hover:bg-slate-800 text-white font-medium py-4 px-8 rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.2)] transition-all flex items-center justify-center gap-3 mx-auto lg:mx-0"
            >
              Conectar meu WhatsApp <ArrowRight size={18} className="transition-transform group-hover:translate-x-1"/>
            </motion.button>
          </div>

          {/* Floating UI Mockup */}
          <div className="flex-1 w-full max-w-lg relative">
            {/* Background decorative elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-radial from-emerald-400/20 via-violet-400/10 to-transparent blur-[60px] rounded-full -z-10" />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, rotate: 2 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-white/70 backdrop-blur-2xl border border-slate-200/60 rounded-[2.5rem] shadow-[0_20px_80px_-20px_rgba(0,0,0,0.15)] ring-1 ring-black/5 p-6 md:p-8"
            >
              {/* WhatsApp App Header Simulation */}
              <div className="flex items-center gap-4 mb-8">
                 <div className="relative">
                   <div className="w-12 h-12 bg-gradient-to-tr from-violet-600 to-indigo-500 rounded-full flex items-center justify-center shadow-lg shadow-violet-500/30">
                     <Bot className="text-white w-6 h-6" />
                   </div>
                   <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
                 </div>
                 <div>
                   <h4 className="font-bold text-slate-900 text-lg tracking-tight">LukIA Assistant</h4>
                   <p className="text-sm text-emerald-600 font-medium tracking-wide">● Online agora</p>
                 </div>
              </div>

              {/* Chat Flow */}
              <div className="space-y-6">
                 {/* User message */}
                 <motion.div 
                   initial={{ opacity: 0, x: 20 }}
                   whileInView={{ opacity: 1, x: 0 }}
                   viewport={{ once: true }}
                   transition={{ delay: 0.4 }}
                   className="flex justify-end"
                 >
                    <div className="bg-[#E6F4EA] text-slate-800 p-4 rounded-3xl rounded-tr-md shadow-sm max-w-[85%] text-[15px] font-medium border border-[#CDE9D5] leading-relaxed">
                      LUK, faz um fechamento flash pra mim: quanto a pista 1 girou hoje de manhã?
                    </div>
                 </motion.div>

                 {/* AI message */}
                 <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1 }}
                    className="flex justify-start"
                 >
                    <div className="bg-white text-slate-700 p-6 rounded-3xl rounded-tl-md shadow-[0_8px_30px_rgba(0,0,0,0.06)] max-w-[95%] text-[15px] border border-slate-100 relative overflow-hidden">
                      {/* Premium card subtle shine */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent opacity-50"></div>
                      
                      <div className="relative z-10">
                        <p className="mb-4 leading-relaxed"><Zap className="inline w-5 h-5 text-amber-500 mr-1 -mt-1"/> Fechamento Flash Concluído! A Pista 1 teve um excelente giro de <strong>3.450 litros</strong>.</p>
                        
                        {/* Summary Widget Inside Chat */}
                        <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/60 text-sm space-y-3 mb-4 backdrop-blur-sm">
                          <div className="flex justify-between items-center"><span className="text-slate-500 flex items-center gap-2"><BarChart3 size={16}/> Comum</span> <span className="font-bold text-slate-900">2.100 L</span></div>
                          <div className="flex justify-between items-center"><span className="text-slate-500 flex items-center gap-2"><BarChart3 size={16}/> Etanol</span> <span className="font-bold text-slate-900">850 L</span></div>
                          <div className="flex justify-between items-center"><span className="text-slate-500 flex items-center gap-2"><BarChart3 size={16}/> S10</span> <span className="font-bold text-slate-900">500 L</span></div>
                        </div>

                        <button className="w-full py-3 bg-violet-50 text-violet-700 font-bold text-sm rounded-xl flex items-center justify-center gap-2 hover:bg-violet-100 transition-colors">
                          <ReceiptCent size={18}/> Gerar NF-e Relatório
                        </button>
                      </div>
                    </div>
                 </motion.div>
              </div>

            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}
